import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, CardHeader, CardTitle, CardBody, Skeleton, Switch, Tabs, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from '../WaapEmptyCard';
import {
  useWaapDataLossRules,
  useToggleWaapDataLossRule,
  type WaapDlpRule,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { useWaapUpgradeGate } from '@/pages/services/waap/use-waap-upgrade-gate';
import styles from './ApiProtectionDataLossPage.module.css';

const TABS = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'rules', label: 'Detection Rules' },
] as const;

const riskTone = (v: string) => (v === 'high' ? 'danger' : v === 'medium' ? 'warning' : 'neutral');

/** Managed Rules — the fixed PII rule catalog. Status toggle + Edit (3-tab editor). */
function ManagedRulesTab({ domainId, serviceId }: { domainId: string; serviceId: string }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const { data, isLoading } = useWaapDataLossRules(domainId);
  const toggleRule = useToggleWaapDataLossRule(domainId);
  // Stable alphabetical order: the vendor list sorts by `updated` desc, so a
  // toggled rule jumps to the top after a reload. Sort by name to keep the
  // catalog order fixed regardless of status changes.
  const rules = useMemo(() => [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [data]);
  const toggle = (r: WaapDlpRule) =>
    toggleRule.mutate(
      { id: r.id, body: { ...r, status: r.status === 1 ? 0 : 1 } },
      { onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }) },
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Managed Rules</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton height={240} />
        ) : rules.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<ShieldOff size={20} />}
            title="No managed rules"
            description="No managed data-loss rules are available for this website."
          />
        ) : (
          <div className={styles.rules}>
            <div className={styles.ruleHead}>
              <span>Sensitivity Data Type</span>
              <span>Risk Level</span>
              <span>Description</span>
              <span>Whitelist</span>
              <span>Status</span>
              <span>Operation</span>
            </div>
            {rules.map((r) => (
              <div key={r.id} className={styles.ruleRow}>
                <span className={styles.name}>{r.name}</span>
                <span>
                  <StatusPill tone={riskTone(r.config.pii_level)}>{r.config.pii_level}</StatusPill>
                </span>
                <span className={styles.muted}>{r.config.description || '—'}</span>
                <span className={styles.muted}>{r.config.whitelist.length}</span>
                <span>
                  <Switch
                    checked={r.status === 1}
                    onCheckedChange={gate('apiProtection', 'API Protection', () => toggle(r))}
                  />
                </span>
                <div className={styles.actions}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/services/waap/${serviceId}/api-protection/data-loss/${r.id}`)}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
      {upgradeDialog}
    </Card>
  );
}

/**
 * API Protection → Data Loss Prevention. Detection Rules (managed PII rules,
 * backed by the private waap2 portal); Overview (native BI charts) is deferred —
 * the vendor's DLP board is broken on the conversant side, so it stays "coming
 * soon". Account-level: the selected website only resolves the Conversant customer.
 */
export function ApiProtectionDataLossPage() {
  // Account-level: any of the account's websites resolves the same customer, so
  // a representative domain is used automatically (no picker / select-website gate).
  const { selected, isLoading: wsLoading } = useWaapWebsite();
  const { serviceId } = useParams<{ serviceId: string }>();
  const domainId = selected?.domainId ?? '';
  const [tab, setTab] = useState<string>('rules');

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'API Protection' }, { label: 'Data Loss Prevention' }]} />}
        title="Data Loss Prevention"
      />

      <Tabs value={tab} onValueChange={setTab} style={{ marginBottom: 'var(--vn-space-4)' }}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {wsLoading ? (
        <Skeleton height={320} />
      ) : tab === 'overview' ? (
        <WaapEmptyCard
          icon={<ShieldOff size={20} />}
          title="Overview coming soon"
          description="The Data Loss Prevention analytics board isn't available yet. It will appear here once ready."
        />
      ) : (
        <ManagedRulesTab domainId={domainId} serviceId={serviceId ?? ''} />
      )}
    </Container>
  );
}
